import { Entity, EntityModel, CreateEntity, UpdateEntity } from './entity.model';

/**
 * Organization roles define the level of access a user has within an organization.
 */
export enum OrganizationRole {
  /** Full admin access: can manage members, settings, and all content */
  Admin = 'admin',
  /** Can create and edit content but cannot manage members or settings */
  Editor = 'editor',
  /** Read-only access to organization content */
  Reader = 'reader'
}

/**
 * Organization entity as returned from the API.
 * Organizations group users and content (prompts and packs).
 */
export interface Organization extends Entity {
  /** Organization name */
  name: string;
  /** Optional description */
  description?: string;
  /** True if this is the user's personal organization */
  is_personal: boolean;
  /** Number of members in the organization */
  member_count: number;
  /** Current user's role in this organization */
  current_user_role: OrganizationRole;
  /** ISO timestamp of creation */
  created_at: string;
  /** ISO timestamp of last update */
  updated_at: string;
}

/**
 * Organization model with convenience methods for permission checking.
 * Extends EntityModel to provide camelCase properties and computed values.
 */
export class OrganizationModel extends EntityModel<Organization> {
  readonly name: string;
  readonly description?: string;
  readonly isPersonal: boolean;
  readonly memberCount: number;
  readonly currentUserRole: OrganizationRole;
  readonly createdAt: Date;
  readonly updatedAt: Date;

  constructor(source: Organization) {
    super(source);
    this.name = source.name;
    this.description = source.description;
    this.isPersonal = source.is_personal;
    this.memberCount = source.member_count;
    this.currentUserRole = source.current_user_role;
    this.createdAt = new Date(source.created_at);
    this.updatedAt = new Date(source.updated_at);
  }

  /**
   * Check if current user is an admin of this organization
   */
  get isAdmin(): boolean {
    return this.currentUserRole === OrganizationRole.Admin;
  }

  /**
   * Check if current user is an editor of this organization
   */
  get isEditor(): boolean {
    return this.currentUserRole === OrganizationRole.Editor;
  }

  /**
   * Check if current user is a reader (read-only) of this organization
   */
  get isReader(): boolean {
    return this.currentUserRole === OrganizationRole.Reader;
  }

  /**
   * Check if current user can create or edit content (prompts/packs)
   * True for admins and editors, false for readers
   */
  get canCreateContent(): boolean {
    return this.isAdmin || this.isEditor;
  }

  /**
   * Check if current user can manage members (add/remove/change roles)
   * True only for admins of non-personal organizations
   */
  get canManageMembers(): boolean {
    return this.isAdmin && !this.isPersonal;
  }

  /**
   * Check if current user can manage organization settings
   * True only for admins of non-personal organizations
   */
  get canManageOrg(): boolean {
    return this.isAdmin && !this.isPersonal;
  }
}

/**
 * Data required to create a new organization
 */
export type CreateOrganization = CreateEntity<Organization> & {
  name: string;
  description?: string;
};

/**
 * Data required to update an existing organization
 */
export type UpdateOrganization = UpdateEntity<Organization> & {
  name?: string;
  description?: string;
};
